import CircleIcon from "@mui/icons-material/Circle";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import ErrorIcon from "@mui/icons-material/Error";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import LinearProgress from "@mui/material/LinearProgress";
import { InlineIcon } from "@iconify/react";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

import { useRecoilValue } from "recoil";

import * as emojiname from "emoji-name-map";
import { toBase64, toBytes } from "fast-base64";
import PouchDB from "pouchdb-browser";

import { useState, useEffect, Children, cloneElement, Fragment } from "react";

import {
  http_post,
  downloadFileFromS3,
  getActiveProject,
  downloadSilentlyForFutureUse,
} from "./Auth";

import {
  imageToggleState,
  peopleDataState,
  activeLabelsState,
  projectPickerState,
} from "./RecoilStates";

import { FileUploadPreview } from "./FileUploadPreview";
import { Pdf } from "./Pdf";
import { TextPreview, codeStyle } from "./TextPreview";
import { Image, ButtonLink, imageTypeWhitelist } from "./Image";

import * as u from "./utility";
import * as riki from "jsriki";

import { DEFAULT_FONT_SIZE } from "./constants";
import { PopupButton, PasswordInput } from "./Password";
import { decrypt } from "./crypto";

export const isGithub = (item) => {
  if ("st_source" in item && item.st_source === "github") {
    return true;
  }
  if ("repository" in item && "html_url" in item.repository) {
    return true;
  }

  return false;
};

export const isGitlab = (item) => {
  if ("st_source" in item && item.st_source === "gitlab") {
    return true;
  }

  if ("repository" in item && "homepage" in item.repository) {
    return (
      item.repository.homepage && item.repository.homepage.includes("gitlab")
    );
  }

  return false;
};

function getMessageText(item) {
  const raw = getRawMessageText(item);
  if (raw.length > 0) {
    return raw;
  }

  return item.st_pp || "";
}

function stringifyPreparsed(item) {
  let res = "";
  for (let i of item) {
    if (i.t == "br") {
      res += "\n";
    } else if (i.t == "q") {
      res += stringifyPreparsed(i.v);
    } else if (i.v && typeof i.v === "string") {
      res += i.v;
    }
  }
  return res;
}

export function rebuildMessageText(item) {
  const raw = getRawMessageText(item);
  if (raw.length > 0) {
    return raw;
  }

  if (item.st_pp) {
    const pp = JSON.parse(item.st_pp);
    let res = stringifyPreparsed(pp);
    return res;
  }

  return "";
}

export const getShortDescription = (item) => {
  let res = "";
  if ("SK4" in item) {
    res += u.getStoryKey(item) + " ";
  }
  res += rebuildMessageText(item);
  return res;
};

function getRawMessageText(item) {
  if ("st_desc" in item) {
    return item.st_desc;
  } else if ("text" in item && item.text) {
    return item.text;
  }

  if ("subtype" in item) {
    if (item.subtype === "message_changed") {
      if (item.message.subtype === "tombstone") {
        return (
          "Message deleted: " +
          ("previous_message" in item && "text" in item.previous_message
            ? item.previous_message.text
            : "")
        );
      }

      return item.message.text;
    } else if (item.subtype === "message_deleted") {
      if ("previous_message" in item) {
        if ("text" in item.previous_message && item.previous_message.text) {
          return `Message deleted: ${item.previous_message.text}`;
        } else if (
          "files" in item.previous_message &&
          item.previous_message.files.length > 0
        ) {
          return `File deleted: ${item.previous_message.files[0].name}`;
        }
      }
      return "Message deleted";
    }
  }

  if (isGithub(item)) {
    if ("commits" in item && item.commits.length) {
      const len = item.commits.length;
      const postfix = len === 2 ? "" : "s";
      const message = item.commits[len - 1].message;
      return message + (len > 1 ? ` (+${len - 1} other${postfix})` : "");
    }

    if ("pull_request" in item) {
      return `PR ${item.action}: ${item.pull_request.title} #${item.number}`;
    }

    if ("comment" in item && "pull_request" in item) {
      return `PR commented: ${item.comment.body}`;
    }

    if ("ref_type" in item) {
      return `${item.ref_type} ${item.ref}`;
    }

    if ("issue" in item) {
      if ("comment" in item) {
        return `commented: issue #${item.issue.number} ${item.comment.body}`;
      }

      if (item.action === "opened") {
        return `${item.action}: issue #${item.issue.number} ${item.issue.title}\n${item.issue.body}`;
      } else {
        return `${item.action}: issue #${item.issue.number} ${item.issue.title}`;
      }
    }
  } else if (isGitlab(item)) {
    if ("commits" in item && item.commits.length) {
      const len = item.commits.length;
      const postfix = len === 2 ? "" : "s";
      const message = item.commits[len - 1].title;
      return message + (len > 1 ? ` (+${len - 1} other${postfix})` : "");
    }

    if ("object_kind" in item) {
      if (item.object_kind === "merge_request") {
        const action = item.object_attributes.action;
        const title = item.object_attributes.title;
        const iid = item.object_attributes.iid;
        return `MR ${action}: ${title} !${iid}`;
      } else if (item.object_kind === "note") {
        if ("merge_request" in item) {
          return `MR commented: ${item.object_attributes.note}`;
        } else {
          return `commented: ${item.object_attributes.note}`;
        }
      } else if (item.object_kind === "issue") {
        const iid = item.object_attributes.iid;
        const title = item.object_attributes.title;
        const desc = item.object_attributes.description;
        if (item.object_attributes.action === "open") {
          return `issue #${iid}: ${title}\n${desc}`;
        } else {
          return `${item.object_attributes.action} issue #${iid}`;
        }
      }
    }
  }

  return "";
}

function isSupportedImage(fileData) {
  const filetypes = imageTypeWhitelist;
  const url = fileData.url_private_download;
  const fileSizeOk = fileData.size < 9 * 1024 * 1024;
  let isImage = filetypes.has(fileData.filetype) && fileSizeOk;

  if (
    !fileData.filetype &&
    fileSizeOk &&
    filetypes.has(u.getExt({ url: url }))
  ) {
    isImage = true;
  }

  if (fileData.filetype === "binary" && fileSizeOk) {
    const ext = u.getExt({ url: url });
    if (ext === "avif") {
      isImage = true;
    }
  }

  return isImage;
}

function getSlackFilesToDownload(files, onlyImages) {
  if (files && files.length > 0) {
    let res = [];
    files.forEach((f, idx) => {
      let isImage = isSupportedImage(f);

      const url = f.url_private_download;
      if (onlyImages && isImage) {
        res.push({
          url: url,
          name: f.name,
          filetype: f.filetype,
          mimetype: f.mimetype,
          index: idx,
        });
      } else if (!onlyImages && !isImage) {
        res.push({
          url: url,
          name: f.name,
          filetype: f.filetype,
          mimetype: f.mimetype,
          index: idx,
        });
      }
    });
    return res;
  }
  return [];
}

function getFilesToDownload(item, onlyImages) {
  let files = [];
  if ("files" in item) {
    files = getSlackFilesToDownload(item.files, onlyImages);
  } else if ("st_files" in item) {
    // topstorie
    let conv = [];
    for (let f of item.st_files) {
      const type =
        u.getExtFromUrlLower(f.uri) || f.type.split("/").slice(-1)[0];
      conv.push({
        url_private_download: f.uri,
        size: f.size,
        filetype: type,
        mimetype: f.type,
        name: f.name,
      });
    }
    files = getSlackFilesToDownload(conv, onlyImages);
  }

  if (
    "old_dummy_message" in item &&
    "files" in item.old_dummy_message &&
    files.length === item.old_dummy_message.files.length
  ) {
    files.forEach((f) => {
      f.uploaded_file = item.old_dummy_message.files[f.index];
    });
  }

  return files;
}

function getAttachmentText(item) {
  if ("attachments" in item && item.attachments.length) {
    if ("fallback" in item.attachments[0]) {
      return item.attachments[0].fallback;
    }
    if ("text" in item.attachments[0]) {
      return item.attachments[0].text;
    }
  }
  return null;
}

function Reaction({ item, fontSize }) {
  let icons = { red_circle: CircleIcon, white_check_mark: CheckBoxIcon };
  let knownIcon = item.reaction in icons;
  const ReactionComponent = knownIcon ? icons[item.reaction] : AddReactionIcon;
  return (
    <Box>
      {knownIcon && <ReactionComponent />}
      {!knownIcon && (
        <Typography sx={{ fontSize: fontSize }}>
          <AddReactionIcon /> reaction: {item.reaction}
        </Typography>
      )}
    </Box>
  );
}

function MessageText({ item, storyKey, fontSize, overflow }) {
  let text = getMessageText(item).trim();

  if (storyKey) {
    text = text
      .replaceAll(`STORY-${storyKey}`, "")
      .replaceAll(`[${storyKey}]`, "")
      .trim();
  }

  const parsed = parseMarkdown(text, item);

  // do not allow scroll if embedded images are present (github images for example)
  let sx = overflow ? { overflow: "auto", maxHeight: 400 } : {};
  for (let m of parsed) {
    if (m.t == "img") {
      sx = {};
      break;
    }
  }

  return (
    <Box sx={sx}>
      <Markdown parsed={parsed} item={item} fontSize={fontSize} start={0} />
      {parsed.length > 0 && <br />}
    </Box>
  );
}

function PictureAttachments({ images, item, onLoad, fontSize }) {
  const toggle = useRecoilValue(imageToggleState);
  const [waiting, setWaiting] = useState(images.length);

  useEffect(() => {
    if (images.length > 0 && onLoad && waiting == 0) {
      onLoad();
    }
  }, [waiting]);

  if (!toggle) {
    return <CropOriginalIcon color="disabled" />;
  }

  if (images.length === 1) {
    return (
      <Image
        image={images[0]}
        item={item}
        index={0}
        onLoad={onLoad}
        fontSize={fontSize}
      />
    );
  }

  const handleLoad = () => {
    if (onLoad) {
      setWaiting((prev) => {
        return prev > 0 ? prev - 1 : 0;
      });
    }
  };

  return (
    <Grid
      container
      direction={"row"}
      justifyItems={"flex-start"}
      alignItems={"flex-start"}
      spacing={0.1}
    >
      {images &&
        images.map((image, i) => (
          <Grid item key={image.index}>
            <Image
              image={image}
              sx={{ mr: 0.5 }}
              item={item}
              index={image.index}
              onLoad={handleLoad}
              fontSize={fontSize}
            />
          </Grid>
        ))}
    </Grid>
  );
}

function FileAttachments({ files }) {
  const handleDownload = (file) => {
    const request = async () => {
      try {
        const path =
          "slackfile?url=" +
          encodeURIComponent(file.url) +
          "&filename=" +
          encodeURIComponent(file.name);
        const response = await http_post(path, null);
        window.location.href = response.data.url;
      } catch (err) {
        window.location.href = file.url;
      } finally {
      }
    };
    request();
  };

  return (
    <Stack gap={1}>
      {files &&
        files.map((file, i) => (
          <Stack key={i} direction="row">
            <DownloadIcon />
            <ButtonLink text={file.name} onclick={() => handleDownload(file)} />
          </Stack>
        ))}
    </Stack>
  );
}

function PdfAttachments({ files, item, fontSize }) {
  return (
    <Stack gap={1}>
      {files &&
        files.map((file, i) => (
          <Stack key={i} direction="row">
            <EmbeddedFile
              file={file}
              filetypes={new Set(["pdf"])}
              item={item}
              encoding="base64"
            >
              <Pdf fontSize={fontSize} />
            </EmbeddedFile>
          </Stack>
        ))}
    </Stack>
  );
}

function TxtAttachments({ files, item, fontSize }) {
  return (
    <Stack gap={1}>
      {files &&
        files.map((file, i) => (
          <Stack key={i} direction="row">
            <EmbeddedFile file={file} item={item}>
              <TextPreview fontSize={fontSize} />
            </EmbeddedFile>
          </Stack>
        ))}
    </Stack>
  );
}

function EncryptedMessage({ message, fontSize, item, storyKey, overflow }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [decrypted, setDecrypted] = useState(null);
  const [error, setError] = useState(false);
  const [decryptInProgress, setDecryptInProgress] = useState(false);

  const handleRequestPassword = (e) => {
    setAnchorEl((prev) => (prev ? null : e.currentTarget));
  };

  const handlePasswordCancel = (e) => {
    setAnchorEl(null);
  };

  const handlePassword = (pass) => {
    const decryptMessage = async () => {
      try {
        const decrypted = await decrypt(pass, message);
        setDecrypted(decrypted);
        setAnchorEl(null);
      } catch (e) {
        setError(true);
      }
      setDecryptInProgress(false);
    };

    setDecryptInProgress(true);
    setTimeout(() => {
      decryptMessage();
    }, 300);
  };

  return (
    <>
      {decrypted && (
        <MessageText
          item={{ ...item, text: decrypted }}
          storyKey={storyKey}
          overflow={overflow}
          fontSize={fontSize}
        />
      )}

      {!decrypted && (
        <Stack
          direction={"row"}
          gap={2}
          sx={{
            ...codeStyle(fontSize),
            position: undefined,
            display: undefined,
            width: "fit-content",
            background: "#7b7b9e",
            pl: 3,
            pt: 1,
            pb: 1,
          }}
          alignItems={"center"}
        >
          <Box>
            <Typography fontSize={fontSize}>
              CONTAINS CONFIDENTIAL MESSAGE
            </Typography>
          </Box>
          <PopupButton
            id={anchorEl ? "encrypted-message-popper" : undefined}
            onClick={handleRequestPassword}
          >
            Reveal
          </PopupButton>
          <Popper
            id={anchorEl ? "encrypted-message-popper" : undefined}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
          >
            <Box
              six={{
                position: "relative",
                display: "inline-block",
              }}
            >
              {decryptInProgress && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "61%",
                    top: "68%",
                    zIndex: 5,
                  }}
                >
                  <CircularProgress size={35} thickness={10} />
                </Box>
              )}

              <PasswordInput
                confirm={false}
                onPassword={handlePassword}
                onCancel={handlePasswordCancel}
                label="Enter password to reveal message"
                error={error}
              />
            </Box>
          </Popper>
        </Stack>
      )}
    </>
  );
}

function ActualPreview({ item, storyKey, fontSize, onLoad, overflow }) {
  const images = getFilesToDownload(item, true);
  const reaction = item.type === "reaction_added";
  const text = !reaction && getMessageText(item);
  const attachmentText = getAttachmentText(item);

  let pdfs = [];
  let txt = [];
  let otherFiles = [];
  for (let f of getFilesToDownload(item, false)) {
    if (
      (f.mimetype && f.mimetype === "application/pdf") ||
      f.filetype === "pdf"
    ) {
      pdfs.push(f);
    } else if (
      f.mimetype &&
      (f.mimetype.startsWith("text/") ||
        f.mimetype === "application/json" ||
        f.mimetype === "application/x-csh" ||
        f.mimetype === "application/x-sh" ||
        f.mimetype === "application/xml" ||
        f.mimetype === "application/javascript" ||
        f.mimetype === "application/x-javascript")
    ) {
      txt.push(f);
    } else {
      otherFiles.push(f);
    }
  }

  useEffect(() => {
    if (onLoad && images.length == 0) {
      onLoad();
    }
  }, [onLoad]);

  return (
    <>
      {reaction && (
        <Reaction item={item} fontSize={fontSize || DEFAULT_FONT_SIZE} />
      )}
      {text && (
        <MessageText
          item={item}
          storyKey={storyKey}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
          overflow={overflow}
        />
      )}

      {images.length > 0 && (
        <PictureAttachments
          images={images}
          item={item}
          onLoad={onLoad}
          fontSize={fontSize}
        />
      )}
      {pdfs.length > 0 && (
        <PdfAttachments
          files={pdfs}
          item={item}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
        />
      )}
      {txt.length > 0 && (
        <TxtAttachments
          files={txt}
          item={item}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
        />
      )}
      {otherFiles.length > 0 && <FileAttachments files={otherFiles} />}
      {attachmentText && (
        <Typography sx={{ fontSize: fontSize || DEFAULT_FONT_SIZE }}>
          {attachmentText}
        </Typography>
      )}
      {item.st_enc && (
        <EncryptedMessage
          message={item.st_enc}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
        />
      )}
    </>
  );
}

function DummyPreview({ item, storyKey, fontSize }) {
  const text = getMessageText(item);

  const isFileUpload = () => {
    return "type" in item && item.type === "topstorie_file_upload";
  };

  return (
    <>
      {text && (
        <>
          {item.error && (
            <Stack direction={"row"} alignItems={"center"} sx={{ mb: 1 }}>
              <ErrorIcon size="large" sx={{ color: "red", mr: 1 }} />
              <Box>
                <MessageText
                  item={item}
                  storyKey={storyKey}
                  fontSize={fontSize || DEFAULT_FONT_SIZE}
                />
              </Box>
            </Stack>
          )}
          {!item.error && (
            <Stack direction={"column"} sx={{ mb: 1 }}>
              <Box sx={{ width: "100%" }}>
                <LinearProgress
                  color="secondary"
                  sx={{
                    backgroundColor: "lightgrey",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "silver",
                    },
                  }}
                />
              </Box>
              <Box>
                <MessageText
                  item={item}
                  storyKey={storyKey}
                  fontSize={fontSize || DEFAULT_FONT_SIZE}
                />
              </Box>
            </Stack>
          )}
        </>
      )}

      {isFileUpload() && item.files.length > 0 && (
        <FileUploadPreview item={item} files={item.files} />
      )}
    </>
  );
}

export function MessagePreview({ item, storyKey, fontSize, overflow }) {
  const [loading, setLoading] = useState(
    !!(item.dummy || item.old_dummy_message)
  );

  const handleLoad = () => {
    setLoading(false);
  };

  return (
    <>
      {(item.dummy || item.old_dummy_message) && (
        <Box sx={{ lineHeight: 1, ...(loading ? {} : { display: "none" }) }}>
          <DummyPreview
            item={item.old_dummy_message || item}
            storyKey={storyKey}
            fontSize={fontSize}
          />
        </Box>
      )}
      {!item.dummy && (
        <Box sx={{ lineHeight: 1, ...(loading ? { display: "none" } : {}) }}>
          <ActualPreview
            item={item}
            storyKey={storyKey}
            fontSize={fontSize}
            onLoad={item.old_dummy_message ? handleLoad : undefined}
            overflow={overflow}
          />
        </Box>
      )}
    </>
  );
}

function EmbeddedFile({ file, filetypes, item, children, encoding }) {
  const [data, setData] = useState(null);
  const [wrongTypeOrError, setWrongTypeOrError] = useState(false);
  const [ext, setExt] = useState(u.getExt(file));

  const downloadFile = async (url, ext) => {
    const downloadS3 = async (url) => {
      let key = "";
      const hash = await u.sha1(url);

      if (ext) {
        // ext is always lower case and in cache we nee the ext from the url verbatum
        key = `${getActiveProject()}/${hash}.${u.getExtFromUrl(url)}`;

        if (filetypes && !filetypes.has(ext)) {
          setWrongTypeOrError(true);
          return null;
        }
      } else {
        const metaKey = `${getActiveProject()}/${hash}.meta`;
        const bytes = await downloadFileFromS3(metaKey);
        const meta = JSON.parse(bytes.toString());

        const type = meta.type.split("/").slice(0)[0];
        const ext = meta.type.split("/").slice(-1)[0];
        if (!filetypes || filetypes.has(meta.type) || filetypes.hash(ext)) {
          setExt(ext);
          key = `${getActiveProject()}/${hash}`;
        } else {
          setWrongTypeOrError(true);
          key = null;
        }
      }

      if (key) {
        const body = await downloadFileFromS3(key);
        if (encoding === "base64") {
          return await toBase64(body);
        } else {
          return body;
        }
      }

      return null;
    };

    let failed = false;
    while (true) {
      try {
        const body = await downloadS3(url);
        setData(body);

        const base64String =
          encoding === "base64" ? body : await toBase64(body);
        const db = new PouchDB(getActiveProject());
        let doc = { _id: url, _attachments: { file: { data: base64String } } };
        if (file.mimetype) {
          doc._attachments.file.content_type = file.mimetype;
        }
        db.get(doc._id)
          .then((prev) => db.put({ _rev: prev._rev, ...doc }).catch((e) => {}))
          .catch((err) => db.put(doc).catch((e) => {}));

        return;
      } catch (err) {
        if (failed) {
          break;
        }
        failed = true;
      }

      try {
        const path = "slackfile?url=" + encodeURIComponent(url);
        await http_post(path, null);
      } catch (err) {
        setWrongTypeOrError(true);
        break;
      }
    }
  };

  const readFileLocalOrRemote = async () => {
    let ok = false;
    if (file.uploaded_file) {
      const readFile = async () => {
        try {
          const buffer = await file.uploaded_file.arrayBuffer();
          let next = new Uint8Array(buffer);
          if (encoding === "base64") {
            next = await toBase64(next);
          }

          setData((prev) => {
            return prev ? prev : next;
          });
          return true;
        } catch (e) {}

        return false;
      };
      ok = await readFile();
      if (ok && file.url) {
        setTimeout(() => {
          downloadSilentlyForFutureUse(file.url, u.getExt(file));
        }, 2000);
      }
    }

    if (!ok && file.url) {
      const ext = u.getExt(file);

      const db = new PouchDB(getActiveProject());
      db.get(file.url, { attachments: true })
        .then((doc) => {
          if (encoding === "base64") {
            setData((prev) => prev || doc._attachments.file.data);
          } else {
            toBytes(doc._attachments.file.data).then((b64) => {
              setData((prev) => prev || b64);
            });
          }
        })
        .catch((err) => {
          downloadFile(file.url, ext);
        });
    }
  };

  useEffect(() => {
    if (!data) {
      readFileLocalOrRemote();
    }
  }, [file, data]);

  const handleError = (e) => {
    setWrongTypeOrError(true);
  };

  const renderChildren = () => {
    return Children.map(children, (child) => {
      return cloneElement(child, {
        data: data,
        file: file,
        item: item,
        onError: handleError,
      });
    });
  };

  return (
    <>
      {!wrongTypeOrError && ext && data && <>{renderChildren()}</>}
      {wrongTypeOrError && <FileAttachments files={[file]} />}
    </>
  );
}

function CodeLine({ fontSize, st_pk, st_sk, children }) {
  const [open, setOpen] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setOpen(false);
  };

  return (
    <Tooltip
      placement="right"
      leaveDelay={400}
      enterDelay={0}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={
        <Fragment>
          <InlineIcon
            icon="solar:copy-bold"
            color="grey"
            style={{ cursor: "pointer", fontSize: "18px", opacity: 0.5 }}
            onClick={handleCopy}
          />
        </Fragment>
      }
      componentsProps={{
        tooltip: {
          sx: { p: 0, backgroundColor: "transparent" },
        },
      }}
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [3, -15],
            },
          },
        ],
      }}
    >
      <Typography
        component="span"
        fontSize={fontSize}
        st_pk={st_pk}
        st_sk={st_sk}
        sx={{
          background: "#d0d0d0",
          color: "#352f2d",
          p: 0.35,
          borderRadius: "3px",
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
}

function CodeBlock({ st_pk, st_sk, children, fontSize }) {
  const [hover, setHover] = useState(false);
  const [anchor, setAnchor] = useState(null);

  return (
    <Box
      sx={codeStyle(fontSize)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setAnchor(null);
      }}
    >
      {hover && (
        <Box sx={{ position: "absolute", right: 0, zIndex: 5 }}>
          <IconButton
            variant="contained"
            size="small"
            onClick={(e) => {
              navigator.clipboard.writeText(children);
              setAnchor(e.currentTarget);
              setTimeout(() => {
                setAnchor(null);
              }, 1000);
            }}
          >
            <ContentCopyIcon fontSize="inherit" sx={{ color: "white" }} />
          </IconButton>
          <Popper
            open={anchor != null}
            anchorEl={anchor}
            placement="right-start"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <Typography fontSize={fontSize * 0.8} sx={{ p: 1 }}>
                    copied!
                  </Typography>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      )}

      <code st_pk={st_pk} st_sk={st_sk}>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: fontSize * 0.85,
          }}
        >
          {children}
        </pre>
      </code>
    </Box>
  );
}

function unescapeSlackEntities(str) {
  return str
    .replaceAll("&amp;", "&")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">");
}

function compactMarkdown(parsed, unescape = false, result = null) {
  let compacted = result || [];

  const pushRaw = (v) => {
    compacted.push(v);
  };

  const pushUnescaped = (v) => {
    if (typeof v.v === "string") {
      v.v = unescapeSlackEntities(v.v);
    }
    pushRaw(v);
  };

  const push = unescape ? pushUnescaped : pushRaw;

  for (let i = 0; i < parsed.length; ++i) {
    const cur = parsed[i];
    if (typeof cur.v === "object") {
      let nested = [];
      compacted.push({ t: cur.t, v: nested });
      compactMarkdown(cur.v, unescape, nested);
    } else if (i == 0) {
      push(parsed[i]);
    } else {
      const prev = compacted[compacted.length - 1];
      if (prev.t === "br" && cur.t === "br") {
        compacted[compacted.length - 1] = { t: "br2" };
      } else {
        push(cur);
      }
    }
  }

  return compacted;
}

function parseMarkdown(text, item) {
  if ("st_pp" in item) {
    if (typeof item.st_pp === "string") {
      const res = compactMarkdown(JSON.parse(item.st_pp));
      return res;
    } else {
      return compactMarkdown(item.st_pp);
    }
  }

  let parsed = riki.parse(text);

  let res = [];
  let styles = [];
  let cur = res;
  for (let i = 0; i < parsed.length / 2; ++i) {
    const t = parsed[i * 2];
    const v = parsed[i * 2 + 1];
    if (t == "clb") {
      cur = [];
      res.push({ t: "cl", v: cur });
    } else if (t == "cle") {
      cur = res;
    } else if (t == "cbb") {
      cur = [];
      res.push({ t: "cb", v: cur });
    } else if (t == "cbe") {
      cur = res;
    } else if (t == "x") {
      styles.push(v);
    } else if (t == "se") {
      styles.pop();
    } else if (t == "q") {
      cur.push({ t: "txt", v: ">" });
    } else if (t == "q2") {
      cur.push({ t: "txt", v: ">>" });
    } else {
      if (styles.length) {
        cur.push({ t: t, v: v, s: styles.join("") });
      } else {
        cur.push({ t: t, v: v });
      }
    }
  }
  return compactMarkdown(res, true);
}

function Markdown({ parsed, item, fontSize, start }) {
  return (
    <>
      {parsed.map((chunk, i) => (
        <MarkdownChunk
          key={start + i}
          type={chunk.t}
          value={chunk.v}
          refs={chunk.r}
          textStyle={chunk.s}
          item={item}
          fontSize={fontSize}
          index={start + i}
          end={start + parsed.length}
        />
      ))}
    </>
  );
}

function StoryReference({ storyId, url, fontSize }) {
  const [story, setStory] = useState(null);
  const [hover, setHover] = useState(false);
  const project = useRecoilValue(projectPickerState);

  const handleMouseEnter = () => {
    const request = async () => {
      try {
        const query = storyId[0] == "[" ? storyId.slice(1, -1) : storyId;
        const req = { search: true, query: query, project: project };
        const resp = await http_post("stories", req);
        const found = resp.data.search;
        if (found) {
          setStory(found[0]);
          return;
        }
      } catch (e) {}
      setStory({ error: true });
    };

    setHover(true);
    if (!story) {
      setTimeout(() => {
        setHover((prev) => {
          if (prev) {
            request();
          }
          return prev;
        });
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  if (story) {
    if (story.error) {
      return <Link href={url}>{storyId}</Link>;
    } else {
      return (
        <Tooltip
          title={getShortDescription(story)}
          enterDelay={800}
          enterNextDelay={800}
        >
          <Link href={url} sx={{ fontSize: fontSize }}>
            {storyId}
          </Link>
        </Tooltip>
      );
    }
  } else {
    return (
      <Link
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        href={url}
        sx={{ fontSize: fontSize }}
      >
        {storyId}
      </Link>
    );
  }
}

function MarkdownChunk({
  type,
  value,
  item,
  refs,
  textStyle,
  fontSize,
  index,
  end,
}) {
  const refStory = { st_pk: item.PK, st_sk: item.SK };

  const urlFromStoryKey = (key) => {
    return (
      window.location.origin +
      `/story/${getActiveProject()}/${key.split("-")[1]}${
        window.location.search
      }`
    );
  };

  const urlFromRef = (ref) => {
    const key = ref.slice(1, -1);
    let url =
      window.location.origin +
      `/story/${getActiveProject()}/${encodeURIComponent(key)}${
        window.location.search
      }`;

    const timeref = item.st_created_at || item.ts || item.event_ts;
    if (timeref) {
      url += `/${encodeURIComponent(timeref)}`;
    }

    return url;
  };

  const urlFromLink = (link, trim) => {
    const trimmed = trim ? link.slice(trim, -trim) : link;
    return trimmed.split("|")[0];
  };

  const annotationFromLink = (link, trim) => {
    const v = trim ? link.slice(trim, -trim) : link;
    const delim = v.indexOf("|");
    if (delim == -1) {
      return v;
    } else {
      const annotation = v.slice(delim + 1);
      return annotation.length ? annotation : v.slice(0, delim);
    }
  };

  const getCode = (value, wrap) => {
    if (typeof value === "string") {
      return value.slice(wrap, -wrap);
    } else {
      let res = "";
      for (let p of value) {
        if (p.t === "txt") {
          res += p.v;
        } else if (p.t === "lnk") {
          res += p.v.slice(1, -1);
        }
      }
      return res;
    }
  };

  const convertTextStyle = (textStyle) => {
    let sx = {};
    if (textStyle) {
      let done = false;
      if (textStyle.includes("_")) {
        sx.fontStyle = "italic";
        done = true;
      }
      if (textStyle.includes("*")) {
        sx.fontWeight = "bold";
        done = true;
      }
      if (textStyle.includes("~")) {
        sx.textDecoration = "line-through";
        done = true;
      }

      if (!done) {
        // previous format
        if (textStyle.includes("i")) {
          sx.fontStyle = "italic";
        }
        if (textStyle.includes("b")) {
          sx.fontWeight = "bold";
        }
        if (textStyle.includes("s")) {
          sx.textDecoration = "line-through";
        }
      }
    }

    return sx;
  };

  if (type === "txt") {
    // text
    return (
      <Typography
        {...refStory}
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        sx={convertTextStyle(textStyle)}
      >
        {value}
      </Typography>
    );
  } else if (type === "stk") {
    // strikethrough -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ textDecoration: "line-through" }}
      >
        {value.slice(1, -1)}
      </Typography>
    );
  } else if (type === "it") {
    // italic -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ fontStyle: "italic" }}
      >
        {value.slice(1, -1)}
      </Typography>
    );
  } else if (type === "bld") {
    // bold -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ fontWeight: "bold" }}
      >
        {value.slice(1, -1)}
      </Typography>
    );
  } else if (type === "url") {
    // raw url
    return (
      <>
        <Link href={value} fontSize={fontSize}>
          {value}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <Link href={urlFromRef(`[${refs[0]}]`)}>[{refs[0]}]</Link>
          </sup>
        )}
      </>
    );
  } else if (type === "br") {
    // line break
    return <br />;
  } else if (type === "br2") {
    // a pair of line breaks
    return (
      <>
        <br />
        <div style={{ fontSize: "30%" }}>
          <br />
        </div>
      </>
    );
  } else if (type === "rk") {
    // reference key like STORY-123
    return (
      <StoryReference
        storyId={value}
        url={urlFromStoryKey(value)}
        fontSize={fontSize}
      />
    );
  } else if (type === "ref") {
    // reference enclosed in square bracket like [123]
    return (
      <StoryReference
        storyId={value}
        url={urlFromRef(value)}
        fontSize={fontSize}
      />
    );
  } else if (type === "lnk") {
    // link enclosed in angle brackets <...>
    return (
      <>
        <Link
          href={urlFromLink(value, 1)}
          sx={convertTextStyle(textStyle)}
          sx={{ fontSize: fontSize }}
        >
          {annotationFromLink(value, 1)}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <Link href={urlFromRef(`[${refs[0]}]`)}>[{refs[0]}]</Link>
          </sup>
        )}
      </>
    );
  } else if (type === "fr") {
    // github's footnote reference
    return (
      <sup style={{ fontSize: fontSize * 0.62 }}>
        <Link href={`#${item.PK}-${item.SK}-footnote-${value}`}>[{value}]</Link>
      </sup>
    );
  } else if (type === "fd") {
    // github's footnote definition
    return (
      <>
        {value == "1" && <Divider />}
        <span id={`${item.PK}-${item.SK}-footnote-${value}`}>
          {value + ". "}
        </span>
      </>
    );
  } else if (type === "footnote") {
    // github's footnote
    return (
      <>
        <br />
        <br />
        <Markdown parsed={value} item={item} fontSize={fontSize} start={end} />
      </>
    );
  } else if (type === "lx") {
    // github's link
    return (
      <>
        <Link
          href={urlFromLink(value, 0)}
          sx={convertTextStyle(textStyle)}
          fontSize={fontSize}
        >
          {annotationFromLink(value, 0)}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <Link href={urlFromRef(`[${refs[0]}]`)}>[{refs[0]}]</Link>
          </sup>
        )}
      </>
    );
  } else if (type === "fx") {
    // github's file attachment
    return (
      <FileAttachments
        files={[
          { url: urlFromLink(value, 0), name: annotationFromLink(value, 0) },
        ]}
      />
    );
  } else if (type === "u") {
    // slack's username
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        <span>
          @<UserName userId={value.slice(2, -1)} />
        </span>
      </Typography>
    );
  } else if (type === "c") {
    // slacks' channel name
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        #<ChannelName channelId={value.slice(2, -1)} />
      </Typography>
    );
  } else if (type === "e") {
    // emoji like :smile:
    return (
      <Typography
        component="span"
        fontSize={fontSize * 1.25}
        style={{ whiteSpace: "pre-wrap", lineHeight: "0em" }}
      >
        {emojiname.get(value) || value}
      </Typography>
    );
  } else if (type === "b") {
    // slack's broadcast like @here
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        @{value.slice(2, -1).split("|").slice(-1)}
      </Typography>
    );
  } else if (type === "s") {
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        @{value.slice(0, -1).split("^").slice(-1)}
      </Typography>
    );
  } else if (type === "cl") {
    // codeline `code`
    return (
      <CodeLine {...refStory} fontSize={fontSize}>
        {getCode(value, 1)}
      </CodeLine>
    );
  } else if (type === "cx") {
    // github's codeline
    return (
      <CodeLine {...refStory} fontSize={fontSize}>
        {value}
      </CodeLine>
    );
  } else if (type === "cb") {
    // codeblock ```code block```
    return (
      <CodeBlock {...refStory} fontSize={fontSize}>
        {u.trimNewLine(getCode(value, 3))}
      </CodeBlock>
    );
  } else if (type === "cbx") {
    //github's code block
    return (
      <CodeBlock {...refStory} fontSize={fontSize}>
        {u.trimNewLine(value)}
      </CodeBlock>
    );
  } else if (type === "q") {
    // quote
    return (
      <Box
        sx={{
          borderLeft: 4,
          borderLeftColor: "dimgrey",
          pl: 1,
          ml: 0.2,
          mt: 0.5,
        }}
      >
        <Markdown parsed={value} item={item} fontSize={fontSize} start={end} />
      </Box>
    );
  } else if (type === "t") {
    // table
    return (
      <TableContainer>
        <Table size="small" sx={{ width: "auto" }}>
          <TableHead>
            <TableRow>
              {value[0].map((cell, i) => (
                <TableCell key={i}>
                  <Markdown
                    parsed={cell}
                    item={item}
                    fontSize={fontSize}
                    start={end}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody stripedRows>
            {value.slice(1).map((row, i) => (
              <TableRow key={i}>
                {row.map((cell, i) => (
                  <TableCell key={i}>
                    <Markdown
                      parsed={cell}
                      item={item}
                      fontSize={fontSize}
                      start={end}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else if (type === "h") {
    return (
      <>
        <Typography
          component="span"
          fontSize={fontSize}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {value}
          {refs && refs.length && (
            <sup style={{ fontSize: fontSize * 0.62 }}>
              <Link href={urlFromRef(`[${refs[0]}]`)}>[{refs[0]}]</Link>
            </sup>
          )}
        </Typography>
      </>
    );
  } else if (type == "img") {
    // github image
    return (
      <>
        <br />
        <Image
          image={{ url: urlFromLink(value, 0) }}
          item={item}
          index={index}
          name={annotationFromLink(value, 0)}
        />
        <br />
      </>
    );
  } else {
    return <div />;
  }
}

export function UserName({ userId }) {
  const data = useRecoilValue(peopleDataState);
  const [user, setUser] = useState(userId);

  useEffect(() => {
    if (data) {
      setUser((prev) => {
        for (let u of data) {
          if (prev === u.id || prev === `${u.app}#${u.id}`) {
            if ("name" in u) {
              return u.name;
            } else if ("email" in u) {
              return u.email;
            } else if ("username" in u) {
              return u.username;
            }
          }
        }

        return prev;
      });
    }
  }, [data]);

  return <>{user}</>;
}

function ChannelName({ channelId }) {
  const data = useRecoilValue(activeLabelsState);
  const [channel, setChannel] = useState(channelId.split("|").slice(-1));

  useEffect(() => {
    if (data) {
      setChannel((prev) => {
        if (prev in data) {
          let out = data[channel].display || data[channel].name;
          if (out.startsWith("#")) {
            return out.slice(1);
          }
          return out;
        }
        return prev;
      });
    }
  }, [data]);

  return <>{channel}</>;
}

export default MessagePreview;

export function getTimestamp(item) {
  if (isGithub(item)) {
    if ("head_commit" in item) {
      return item.head_commit.timestamp;
    }

    if ("pull_request" in item && item["action"] === "opened") {
      return item.pull_request.created_at;
    }

    if ("pull_request" in item && item["action"] === "closed") {
      return item.pull_request.closed_at;
    }

    if ("pull_request" in item) {
      return item.pull_request.updated_at;
    }

    if ("comment" in item) {
      return item.comment.created_at;
    }

    if ("issue" in item && item["action"] === "opened") {
      return item.issue.created_at;
    }

    if ("issue" in item && item["action"] === "opened") {
      return item.issue.created_at;
    }

    if ("issue" in item && item["action"] === "closed") {
      return item.issue.closed_at;
    }

    if ("issue" in item) {
      return item.issue.updated_at;
    }

    if ("commits" in item && item.commits.length) {
      return item.commits[0].timestamp;
    }
  } else if (isGitlab(item)) {
    if ("commits" in item && item.commits.length) {
      return item.commits[0].timestamp;
    }
    if ("object_attributes" in item) {
      if ("updated_at" in item.object_attributes)
        return item.object_attributes.updated_at;

      if ("timestamp" in item.object_attributes) {
        return item.object_attributes.timestamp;
      }
    }
  }

  // topstorie and slack
  if ("st_created_at" in item) {
    return u.timestampToString(item.st_created_at);
  } else if ("ts" in item) {
    return u.timestampToString(item.ts);
  }
  if ("event_ts" in item) {
    return u.timestampToString(item.event_ts);
  }

  return "";
}

export function getUser(item) {
  if ("st_user" in item && "st_source" in item) {
    return `${item.st_source}#${item.st_user}`;
  }

  if (isGitlab(item)) {
    if ("user" in item) {
      return item.user.name;
    }
    if ("commits" in item && item.commits.length) {
      if (item.commits.length && "author" in item.commits[0]) {
        return item.commits[item.commits.length - 1].author.name;
      }
    }
    if ("user_name" in item) {
      return item.user_name;
    }
  } else if (isGithub(item)) {
    if ("user" in item) {
      return item.user;
    }

    if ("commits" in item && item.commits.length) {
      if (item.commits.length && "author" in item.commits[0]) {
        return item.commits[item.commits.length - 1].author.name;
      }
    }

    if ("sender" in item) {
      return item.sender.login;
    }

    if ("pusher" in item) {
      return item.pusher.name;
    }
  } else {
    // slack
    if ("user" in item) {
      return item.user;
    }

    if ("message" in item && "user" in item.message) {
      return item.message.user;
    }

    if ("message" in item && "edited" in item.message) {
      return item.message.edited.user;
    }
  }

  const src = item.st_source;
  if (src) {
    return src === "auto" ? "automatic" : src;
  }

  return "";
}

export const getSourceUrl = (item) => {
  if (item.st_url) {
    return item.st_url;
  }

  if (isGithub(item)) {
    if ("commits" in item) {
      return item.commits[item.commits.length - 1].url;
    } else if ("pull_request" in item) {
      return item.pull_request.html_url;
    } else if ("comment" in item) {
      return item.comment.html_url;
    } else if ("ref_type" in item && item["ref_type"] === "branch") {
      return item.repository.html_url + "/tree/" + item.ref;
    } else if ("issue" in item) {
      return item.issue.html_url;
    }
  } else if (isGitlab(item)) {
    if ("commits" in item && item.commits.length) {
      return item.commits[item.commits.length - 1].url;
    } else if ("merge_request" in item) {
      return item.merge_request.url;
    } else if ("object_attributes" in item) {
      return item.object_attributes.url;
    }
  }

  return null;
};
